/**
 * SlideAlertBox + SlideAlertPanel
 * Dark-themed alert/insight boxes with configurable grid layout.
 *
 * Usage:
 *   <SlideAlertPanel columns={2}>
 *     <SlideAlertBox headline="Title">Body text with <DarkInsightEmphasis>emphasis</DarkInsightEmphasis></SlideAlertBox>
 *     <SlideAlertBox headline="Title">...</SlideAlertBox>
 *   </SlideAlertPanel>
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';

// ============================================
// Panel — configurable grid wrapper
// ============================================

const PanelGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, 1fr);
  gap: ${spacing.sm};
  align-content: center;
  width: 100%;
`;

interface SlideAlertPanelProps {
  /** Number of columns (default 1). Use 2 for a 2×N grid. */
  columns?: number;
  className?: string;
  children: React.ReactNode;
}

export const SlideAlertPanel: React.FC<SlideAlertPanelProps> = ({ columns = 1, className, children }) => (
  <PanelGrid $columns={columns} className={className}>{children}</PanelGrid>
);

// ============================================
// Alert Box — dark gradient card
// ============================================

const BoxWrapper = styled.div`
  position: relative;
  background: linear-gradient(135deg, ${colors.highlight.dark} 0%, ${colors.highlight.light} 100%);
  border: 1px solid ${colors.brand.primaryDark};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.md} ${spacing.xl};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;

  &::after {
    content: '';
    position: absolute;
    inset: 4px;
    border: 1px solid ${colors.brand.extraLight}40;
    border-radius: calc(${borderRadius.xl} - 3px);
    pointer-events: none;
  }
`;

const Headline = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.sm}, 1.2vw, ${typography.fontSize.base});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.accent.coral.extraDark};
  margin-bottom: 4px;
`;

const Body = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.2vw, ${typography.fontSize.base});
  color: rgba(255, 255, 255, 0.85);
  line-height: ${typography.lineHeight.relaxed};
`;

interface SlideAlertBoxProps {
  headline: string;
  children: React.ReactNode;
}

export const SlideAlertBox: React.FC<SlideAlertBoxProps> = ({ headline, children }) => (
  <BoxWrapper>
    <Headline>{headline}</Headline>
    <Body>{children}</Body>
  </BoxWrapper>
);
